import { useState, useMemo } from 'react';
import { Timecode } from '../types';

const DEFAULT_FRAME_RATE = 24;

/**
 * Epsilon tolerance for floating-point comparisons.
 * Accounts for browser floating-point drift in video.currentTime.
 */
const FRAME_EPSILON = 0.0001;

export const timeToFrame = (timeInSeconds: number, fps: number = DEFAULT_FRAME_RATE): number => {
  const clampedTime = Math.max(0, timeInSeconds);
  return Math.floor((clampedTime + FRAME_EPSILON) * fps);
};

export const frameToTime = (frame: number, fps: number = DEFAULT_FRAME_RATE): number => {
  return frame / fps;
};

export const clampFrame = (frame: number, minFrame: number, maxFrame: number): number => {
  return Math.max(minFrame, Math.min(maxFrame, frame));
};

export const getMaxSeekTime = (duration: number, fps: number = DEFAULT_FRAME_RATE): number => {
  if (duration <= 0) return 0;
  const totalFrames = timeToFrame(duration, fps);
  const lastFrameStart = frameToTime(Math.max(0, totalFrames - 1), fps);
  const frameDuration = 1 / fps;
  return lastFrameStart + (frameDuration / 2);
};

export const snapToFrame = (timeInSeconds: number, frameRate: number = DEFAULT_FRAME_RATE): number => {
  const frame = timeToFrame(timeInSeconds, frameRate);
  return frameToTime(frame, frameRate);
};

export const secondsToTimecode = (timeInSeconds: number, frameRate: number = DEFAULT_FRAME_RATE): Timecode => {
  const snappedTime = snapToFrame(timeInSeconds, frameRate);
  const totalSeconds = Math.floor(snappedTime);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const frameDecimal = snappedTime - totalSeconds;
  const frames = Math.round(frameDecimal * frameRate);

  return { minutes, seconds, frames };
};

export const formatTimecode = (tc: Timecode): string => {
  const mm = String(tc.minutes).padStart(2, '0');
  const ss = String(tc.seconds).padStart(2, '0');
  const ff = String(tc.frames).padStart(2, '0');
  return `${mm}:${ss}:${ff}`;
};

export const timecodeToSeconds = (tc: Timecode, frameRate: number = DEFAULT_FRAME_RATE): number => {
  return tc.minutes * 60 + tc.seconds + tc.frames / frameRate;
};

export const useTimecode = (initialTime: number = 0, frameRate: number = DEFAULT_FRAME_RATE) => {
  const [timeInSeconds, setTimeInSeconds] = useState(initialTime);

  const timecode: Timecode = useMemo(() => secondsToTimecode(timeInSeconds, frameRate), [timeInSeconds, frameRate]);
  const formattedTimecode = useMemo(() => formatTimecode(timecode), [timecode]);

  const seekToTimecode = (tc: Timecode): number => {
    return tc.minutes * 60 + tc.seconds + tc.frames / frameRate;
  };

  return {
    timeInSeconds,
    setTimeInSeconds,
    timecode,
    formattedTimecode,
    formatTimecode,
    seekToTimecode,
    frameRate,
  };
};
